import React, { CSSProperties, Fragment, useContext, useEffect, useRef } from "react"
import { flexRender, Column, Table } from "@tanstack/react-table"
import { TableBody, TableCell, TableRow } from "../table"
import { DictType } from "@pma/types"
import Loader from "../loader"
import AddTaskInput from "@pma/components/projects/add-task-input"
import useTranslator from "@pma/hooks/use-translator"
import { useSortable } from "@dnd-kit/sortable"
import DraggableRow from "@pma/components/projects/graggable-tasks/DraggableRow"
import { GroupedTasksContext } from "@pma/components/projects/grouped-tasks/context/GroupedTasksProvider"

interface TableBodyProps {
  table: Table<DictType>
  tableContainerRef?: React.RefObject<HTMLDivElement>
  showLoader: boolean
  projectId: string | undefined
  columns: any
  getCommonPinningStyles: (column: Column<DictType>) => CSSProperties
  context?: "tasks" | "projects" | "users"
  group_id?: number | null | undefined
  highlightedTaskId?: number | null
  onRowClick?: (row: DictType) => void
  dataLength?: number
  iszIndexApply?: boolean
}

// Define props interface for EmptyRow
interface EmptyRowProps {
  id: string
  colSpan: number
  projectId: string | undefined
  group_id: number | null | undefined
}

const EmptyRow = ({ id, colSpan, projectId, group_id }: EmptyRowProps) => {
  const { translate } = useTranslator()
  const { setNodeRef, attributes, listeners, isOver } = useSortable({ id })

  return (
    <TableRow
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`border-2 border-dashed ${
        isOver ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50"
      }`}
      style={{ minHeight: "100px" }}
    >
      <TableCell colSpan={colSpan}>
        <div className='text-gray-500'>{translate("Drop tasks here")}</div>
        <AddTaskInput projectId={projectId} group_id={group_id} />
      </TableCell>
    </TableRow>
  )
}

function TBody({
  table,
  group_id,
  showLoader,
  projectId,
  columns,
  getCommonPinningStyles,
  context,
  highlightedTaskId,
  onRowClick,
  dataLength = 0,
  iszIndexApply = false,
}: TableBodyProps) {
  const { translate, locale } = useTranslator()
  const { rows } = table.getRowModel()
  const rowRefs = useRef<Record<any, HTMLTableRowElement | null>>({})

  const { lastInsertTaskId } = useContext(GroupedTasksContext) as {
    lastInsertTaskId: number
  }

  useEffect(() => {
    if (lastInsertTaskId !== null && rowRefs.current[lastInsertTaskId]) {
      const rowElement = rowRefs.current[lastInsertTaskId]
      rowElement?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }, [table.getRowModel().rows, lastInsertTaskId])

  return (
    <TableBody>
      {showLoader ? (
        <TableRow>
          <TableCell colSpan={columns.length} className='h-24 text-center'>
            <div className='flex h-[20vh]'>
              <Loader />
            </div>
          </TableCell>
        </TableRow>
      ) : (
        <>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows?.map((row, index) => {
              return (
                <Fragment key={row.id}>
                  <DraggableRow
                    row={row}
                    getCommonPinningStyles={getCommonPinningStyles}
                    group_id={group_id}
                    className={
                      row.original.id === highlightedTaskId ? "animate-pulse bg-slate-200" : row.original.deleted ? "bg-red-100" : ""
                    }
                    context={context}
                    onRowClick={onRowClick}
                    dataLength={dataLength}
                    iszIndexApply={iszIndexApply}
                  />
                  {row.getIsExpanded() && context === "tasks" && (
                    <TableRow>
                      <td className='' colSpan={row.getVisibleCells().length}>
                        <AddTaskInput task={row.original} group_id={group_id} />
                      </td>
                    </TableRow>
                  )}
                  {/* Add task to project */}
                  {table.getRowModel().rows.length === index + 1 && context === "tasks" && (
                    <tr
                      key={row.id}
                      ref={(el) => {
                        rowRefs.current[row.original.id] = el
                      }}
                      tabIndex={-1} // Allow focus
                    >
                      <td colSpan={row.getVisibleCells().length}>
                        <AddTaskInput projectId={projectId} group_id={group_id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })
          ) : (
            <>
              {context === "tasks" ? (
                <EmptyRow
                  id={`empty_${group_id ?? -1}`}
                  colSpan={columns.length}
                  projectId={projectId}
                  group_id={group_id}
                />
              ) : (
                <TableCell colSpan={columns.length} className='h-24 text-center'>
                  {translate("No results")}.
                </TableCell>
              )}
            </>
          )}
        </>
      )}
    </TableBody>
  )
}

export default TBody
